import { useEffect, useState } from 'react'

import { RichMarkdownEditor } from '@/components/RichMarkdownEditor'
import { StatusLabelStacked } from '@/components/StatusLabelStacked'
import { UnderlineDateInput } from '@/components/ui/underline-date-input'
import {
  statusIsDestructive,
  statusReasonPrompt,
  statusRecordsAnEventTime,
  statusRequiresReason,
} from '@/db/domain/statusRules'
import { formatServiceStatus } from '@/help/serviceStatus'
import { fromUtcIsoToZaParts, fromZaLocalToUtcIso } from '@/lib/datetime'
import { getServiceStatusMeta } from '@/lib/serviceStatusMeta'
import type { ServiceStatus } from '@/lib/schemas/db'

export interface ChangeStatusDialogProps {
  open: boolean
  /** Public ref shown in the header, e.g. 'J5H-Q4TV'. */
  refLabel: string
  /** Usually the customer name. */
  contextLabel?: string
  currentStatus: ServiceStatus
  /** Statuses offered in the "Move to" list, from the domain transition map. */
  options: ServiceStatus[]
  /**
   * Whether the picked target demands a reason. Defaults to the domain rule;
   * override only to require a reason in *more* cases, never fewer.
   */
  requiresReason?: (target: ServiceStatus) => boolean
  onClose: () => void
  /**
   * Awaited before the dialog closes; reason is trimmed ('' when empty).
   * `occurredAt` is a UTC ISO string when the user back-dated the event,
   * undefined when it happened now (the default).
   */
  onConfirm: (
    status: ServiceStatus,
    reason: string,
    occurredAt?: string,
  ) => void | Promise<void>
}

/**
 * The deliberate status-change modal shared by the Services side panel and
 * the Log Work tabs: pick a target status, give a reason where needed,
 * confirm.
 */
export function ChangeStatusDialog({
  open,
  refLabel,
  contextLabel,
  currentStatus,
  options,
  requiresReason = statusRequiresReason,
  onClose,
  onConfirm,
}: ChangeStatusDialogProps) {
  const [target, setTarget] = useState<ServiceStatus | ''>('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // ZA local parts (the office's timezone), converted to UTC on confirm.
  // Pre-filled with now when an event status is picked; `edited` stays false
  // until the user actually changes it, so an untouched field means "occurred
  // when recorded" and no event time is stored.
  const [occurredDay, setOccurredDay] = useState('')
  const [occurredTime, setOccurredTime] = useState('')
  const [occurredEdited, setOccurredEdited] = useState(false)

  const fillOccurredWithNow = () => {
    const nowParts = fromUtcIsoToZaParts(new Date().toISOString())
    setOccurredDay(nowParts.day)
    setOccurredTime(nowParts.time)
    setOccurredEdited(false)
  }

  useEffect(() => {
    if (open) {
      setTarget('')
      setReason('')
      setSaving(false)
      setError(null)
      setOccurredDay('')
      setOccurredTime('')
      setOccurredEdited(false)
    }
  }, [open])

  // Picking an event status pre-fills "now", so the common case needs no typing
  // and the uncommon one (it happened earlier) is one edit away.
  useEffect(() => {
    if (target !== '' && statusRecordsAnEventTime(target)) {
      const nowParts = fromUtcIsoToZaParts(new Date().toISOString())
      setOccurredDay(nowParts.day)
      setOccurredTime(nowParts.time)
      setOccurredEdited(false)
    }
  }, [target])

  if (!open) return null

  // Only statuses that mark a real-world moment (Arrived/LeftSite,
  // Started/Ended) ask when they occurred; the rest are office decisions whose
  // event time is simply when they were recorded.
  const offersEventTime = target !== '' && statusRecordsAnEventTime(target)
  const occurredAtIso =
    offersEventTime && occurredEdited && occurredDay !== '' && occurredTime !== ''
      ? fromZaLocalToUtcIso(occurredDay, occurredTime)
      : undefined
  const occurredIncomplete =
    offersEventTime && occurredEdited && (occurredDay === '' || occurredTime === '')
  const occurredInFuture =
    occurredAtIso !== undefined && new Date(occurredAtIso).getTime() > Date.now()

  const todayZa = fromUtcIsoToZaParts(new Date().toISOString()).day

  // A same-status move exists only to update the reason, so it always needs one.
  // The domain rule is a floor: a caller may demand a reason in more cases
  // (leaving a locked record) but never waive one the domain insists on.
  const reasonRequired =
    target !== '' &&
    (target === currentStatus ||
      requiresReason(target) ||
      statusRequiresReason(target))

  // What to ask for, and the warning under the field. Comes from the domain so
  // a new status brings its own wording instead of falling through to generic
  // copy that asks the wrong question.
  const reasonPrompt =
    target !== '' && reasonRequired ? statusReasonPrompt(target) : undefined

  // Cancelling closes the record for good, so its confirmation should not look
  // like the routine moves sitting next to it in the same list.
  const isDestructive = target !== '' && statusIsDestructive(target)
  const confirmEnabled =
    target !== '' &&
    (!reasonRequired || reason.trim() !== '') &&
    !occurredInFuture &&
    !occurredIncomplete &&
    !saving

  const handleConfirm = async () => {
    if (!target || !confirmEnabled) return
    setSaving(true)
    setError(null)
    try {
      await onConfirm(target, reason.trim(), occurredAtIso)
      onClose()
    } catch (err) {
      // Surface the failure instead of silently leaving the modal open — that
      // read as "the status change did nothing" and left staff re-picking.
      setError(err instanceof Error ? err.message : 'Could not change the status.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/45 p-6">
      <div className="w-[440px] max-w-full overflow-hidden rounded-[14px] bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-[22px] pt-[18px] pb-3.5">
          <h2 className="m-0 mb-1 text-base font-semibold">Change status</h2>
          <p className="m-0 text-[12.5px] text-slate-500">
            <span className="font-mono text-blue-700">{refLabel}</span>
            {contextLabel ? <> · {contextLabel}</> : null}
          </p>
          <div className="mt-2.5 inline-flex items-center gap-1.5 text-xs text-slate-600">
            <span className="text-slate-400">Currently</span>
            <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
              <span
                className={`h-2 w-2 rounded-full ${getServiceStatusMeta(currentStatus).dotClass}`}
              />
              <StatusLabelStacked status={currentStatus} />
            </span>
          </div>
        </div>

        <div className="px-[22px] py-4">
          <div className="mb-2 text-[10.5px] tracking-wider text-slate-400 uppercase">
            Move to
          </div>
          <div className="flex max-h-[38vh] flex-col gap-1.5 overflow-auto">
            {options.map((status) => {
              const isPicked = status === target
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => setTarget(status)}
                  className={`flex w-full cursor-pointer items-center justify-between rounded-[9px] px-3 py-[9px] text-left text-[12.5px] font-medium ${
                    isPicked
                      ? 'border-2 border-blue-500 bg-blue-50 text-slate-900'
                      : 'border border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full ${getServiceStatusMeta(status).dotClass}`}
                    />
                    <StatusLabelStacked status={status} />
                    {status === currentStatus ? (
                      <span className="font-normal text-slate-400">
                        (update reason)
                      </span>
                    ) : null}
                  </span>
                  <span className="font-bold text-blue-500">{isPicked ? '✓' : ''}</span>
                </button>
              )
            })}
          </div>

          <div className="mt-3.5">
            <div className="mb-1.5 text-[10.5px] tracking-wider text-slate-400 uppercase">
              Reason {reasonRequired ? '(required)' : '(optional)'}
            </div>
            {/* Remounted per target status so switching statuses clears the
                reason the editor is holding, matching the `reason` reset. */}
            <RichMarkdownEditor
              key={target}
              initialValue=""
              onChange={setReason}
              placeholder={
                reasonPrompt?.placeholder ?? 'Optional context for the office team'
              }
              ariaLabel="Reason"
              contentClassName="min-h-12 text-[12.5px]"
            />
            {reasonPrompt ? (
              <p className="m-0 mt-1.5 text-[11px] text-rose-700">
                {reasonPrompt.warning}
              </p>
            ) : null}
          </div>

          <div className={offersEventTime ? 'mt-3.5' : 'hidden'}>
            <div className="mb-1.5 flex items-baseline justify-between gap-2">
              <div className="text-[10.5px] tracking-wider text-slate-400 uppercase">
                Occurred at
              </div>
              {occurredEdited ? (
                <button
                  type="button"
                  onClick={fillOccurredWithNow}
                  className="cursor-pointer text-[11.5px] text-blue-700 underline"
                >
                  Now
                </button>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <UnderlineDateInput
                value={occurredDay}
                max={todayZa}
                onChange={(event) => {
                  setOccurredDay(event.target.value)
                  setOccurredEdited(true)
                }}
                aria-label="Date the status occurred"
                className="w-[11rem] rounded-lg border border-slate-300 bg-white px-2"
                inputClassName="h-[32px] text-[12.5px] shadow-none focus:shadow-none"
              />
              <input
                type="time"
                value={occurredTime}
                onChange={(event) => {
                  setOccurredTime(event.target.value)
                  setOccurredEdited(true)
                }}
                aria-label="Time the status occurred"
                className="h-[34px] rounded-lg border border-slate-300 px-2 text-[12.5px] text-slate-900"
              />
            </div>
            {occurredInFuture ? (
              <p className="m-0 mt-1.5 text-[11px] text-rose-700">
                The occurred-at time can’t be in the future.
              </p>
            ) : occurredIncomplete ? (
              <p className="m-0 mt-1.5 text-[11px] text-rose-700">
                Give both a date and a time, or press Now.
              </p>
            ) : occurredEdited ? (
              <p className="m-0 mt-1.5 text-[11px] text-slate-500">
                The change is still recorded now — this is when it occurred.
              </p>
            ) : null}
          </div>
        </div>

        {error ? (
          <div className="mx-[22px] mb-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-slate-100 px-[22px] pt-3 pb-[18px]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-[34px] cursor-pointer rounded-lg border border-slate-300 bg-white px-3.5 text-[12.5px] font-medium text-slate-600"
          >
            Keep as is
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={!confirmEnabled}
            className={`inline-flex h-[34px] items-center gap-2 rounded-lg border-none px-4 text-[12.5px] font-semibold ${
              confirmEnabled
                ? isDestructive
                  ? 'cursor-pointer bg-red-600 text-white hover:bg-red-700'
                  : 'cursor-pointer bg-slate-900 text-white'
                : 'cursor-not-allowed bg-slate-200 text-slate-400'
            }`}
          >
            {saving ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : null}
            {target
              ? target === currentStatus
                ? 'Update reason'
                : `Move to ${formatServiceStatus(target)}`
              : 'Choose a status'}
          </button>
        </div>
      </div>
    </div>
  )
}
